import React, { useState } from "react";
import * as Phosphor from "@phosphor-icons/react";
import { ArrowRight, Warning, CheckCircle } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { Overline, Container, btn } from "@/components/ui-kit";
import { Field, TextInput, TextArea, NativeSelect } from "@/components/forms";
import AntiSpam from "@/components/AntiSpam";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import { toast } from "sonner";

const Icon = ({ name, ...props }) => {
  const C = Phosphor[name] || Phosphor.Circle;
  return <C {...props} />;
};

const FOCUS = [
  { icon: "MapTrifold", title: "Land Acquisition", desc: "Acquiring strategically located parcels positioned for residential growth across the Charlotte region." },
  { icon: "Buildings", title: "Residential Development", desc: "Advancing well-located land through entitlement and horizontal development into residential communities." },
  { icon: "Target", title: "Project-Specific Investments", desc: "Participation in individually evaluated projects, considered on their own merits." },
  { icon: "Handshake", title: "Joint Ventures", desc: "Aligned partnerships structured around a specific opportunity, timeline, and risk profile." },
  { icon: "HouseLine", title: "Builder Partnerships", desc: "Finished-lot and take-down structures that pair our land with builders' execution." },
  { icon: "UsersThree", title: "Strategic Development Partnerships", desc: "Longer-term relationships with capital and operating partners who share our approach." },
];

const APPROACH = [
  { icon: "MagnifyingGlass", title: "Disciplined Underwriting", desc: "Every opportunity is assessed on feasibility, market demand, cost, risk, and structure before we proceed." },
  { icon: "Scales", title: "Alignment of Interest", desc: "We structure participation so that our interests are aligned with those of our partners." },
  { icon: "Compass", title: "Local Market Expertise", desc: "Deep familiarity with Charlotte-area growth, zoning, infrastructure, and development trends." },
  { icon: "ChatCircleText", title: "Transparent Communication", desc: "Clear, straightforward dialogue throughout evaluation, structuring, and execution." },
];

const PARTNER_TYPES = ["Individual Investor", "Family Office", "Institutional Investor", "Builder", "Developer", "Other"];
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function InvestmentOpportunities() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", partner_type: "", message: "" });
  const [spam, setSpam] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.name.trim()) err.name = "Required.";
    if (!emailOk(form.email)) err.email = "Valid email required.";
    if (!form.message.trim()) err.message = "Please add a brief note.";
    setErrors(err);
    if (Object.keys(err).length) return;
    if (!spam.captcha_answer || spam.captcha_answer !== spam.captcha_a + spam.captcha_b) {
      toast.error("Please answer the verification question correctly.");
      return;
    }
    setSubmitting(true);
    try {
      const message = `${form.partner_type ? `[Partner type: ${form.partner_type}] ` : ""}${form.message}`;
      await api.post("/submissions/contact", {
        name: form.name, company: form.company, email: form.email, phone: form.phone,
        reason: "Investment Opportunity", message, ...spam,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToInquiry = (e) => {
    e.preventDefault();
    document.getElementById("inquire")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="investment-opportunities-page">
      <Seo
        title="Investment Opportunities — Partner With Redwood Capital"
        description="Redwood Capital evaluates residential land and development opportunities and may work with qualified investors and strategic partners on selected Charlotte-area projects."
        path="/investment-opportunities"
      />
      <PageHero
        overline="Investment Opportunities"
        title="Invest Alongside Redwood Capital"
        subtitle="A real estate investment and development company that may work with qualified investors and strategic partners on selected residential land and development opportunities across the Charlotte region."
        image={IMAGES.skyline}
      />

      {/* OVERVIEW */}
      <section className="py-20 md:py-28" data-testid="investment-overview">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
            <Reveal className="lg:col-span-7">
              <Overline>Overview</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight">
                Selective, project-based participation
              </h2>
              <p className="mt-6 text-ink text-lg leading-relaxed">
                Redwood Capital evaluates residential land and development opportunities and may partner with
                qualified investors and strategic partners on selected projects. Participation is considered on a
                case-by-case basis and only where interests are aligned.
              </p>
              <p className="mt-4 text-ink text-lg leading-relaxed">
                We do not publish live offerings on this website. Prospective partners are encouraged to introduce
                themselves so we can share relevant information as appropriate opportunities arise.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="bg-forest text-white rounded-sm p-8 md:p-10 relative overflow-hidden">
                <div className="grain" />
                <div className="relative">
                  <h3 className="font-serif text-2xl font-semibold">Introduce your firm</h3>
                  <p className="mt-3 text-white/65 leading-relaxed">
                    Tell us about your investment or partnership interests and we'll be in touch to discuss whether
                    there's a fit.
                  </p>
                  <a href="#inquire" onClick={scrollToInquiry} className={`${btn.gold} mt-6`} data-testid="invest-discuss-btn">
                    Discuss Investment Opportunities <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* FOCUS AREAS */}
      <section className="bg-white border-y border-line py-20 md:py-28" data-testid="investment-focus-areas">
        <Container>
          <Reveal>
            <Overline>Areas of Focus</Overline>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight max-w-2xl">
              Where we may work with partners
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FOCUS.map((o, i) => (
              <Reveal key={o.title} delay={(i % 3) * 0.08}>
                <div className="relative h-full bg-cream border border-line rounded-sm p-8 overflow-hidden transition-all duration-300 hover:border-gold hover:shadow-[0_14px_36px_rgba(13,46,27,0.08)] group">
                  <span className="absolute top-6 right-7 font-serif text-3xl text-forest/5 group-hover:text-gold/25 transition-colors duration-300 select-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-forest/5 group-hover:bg-gold/15 transition-colors duration-300">
                    <Icon name={o.icon} size={26} weight="light" className="text-forest" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-forest">{o.title}</h3>
                  <p className="mt-3 text-ink leading-relaxed text-[15px]">{o.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* APPROACH */}
      <section className="relative bg-forest text-white py-20 md:py-28 overflow-hidden" data-testid="investment-approach">
        <img src={IMAGES.skyline} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.1]" />
        <div className="grain" />
        <Container className="relative">
          <Reveal>
            <Overline>Our Approach</Overline>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
              An institutional mindset applied locally
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
            {APPROACH.map((p, i) => (
              <Reveal key={p.title} delay={(i % 2) * 0.1}>
                <div className="flex gap-5">
                  <div className="shrink-0 w-14 h-14 flex items-center justify-center rounded-sm bg-white/5 border border-white/10">
                    <Icon name={p.icon} size={26} weight="light" className="text-gold" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-white">{p.title}</h3>
                    <p className="mt-2 text-white/65 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* INQUIRY FORM */}
      <section className="py-20 md:py-28" data-testid="investor-inquiry">
        <Container>
          <div id="inquire" className="max-w-3xl mx-auto scroll-mt-28">
            {done ? (
              <div className="bg-white border border-line rounded-sm p-10 text-center" data-testid="investor-success">
                <div className="mx-auto w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center">
                  <CheckCircle size={40} weight="light" className="text-gold" />
                </div>
                <h3 className="mt-6 font-serif text-2xl font-semibold text-forest">Thank you</h3>
                <p className="mt-3 text-ink">We've received your inquiry and will follow up to discuss potential opportunities.</p>
              </div>
            ) : (
              <>
                <Reveal>
                  <div className="text-center mb-10">
                    <Overline>Prospective Partners</Overline>
                    <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight">
                      Discuss Investment Opportunities
                    </h2>
                    <p className="mt-4 text-ink text-lg leading-relaxed max-w-xl mx-auto">
                      A brief introduction is all we need to begin a conversation. All inquiries are treated
                      confidentially.
                    </p>
                  </div>
                </Reveal>
                <Reveal delay={0.1}>
                  <form onSubmit={submit} className="bg-white border border-line rounded-sm p-6 sm:p-10 shadow-[0_12px_40px_rgba(13,46,27,0.08)] space-y-6" data-testid="investor-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Name" required error={errors.name}>
                        <TextInput value={form.name} onChange={set("name")} error={errors.name} data-testid="investor-name" />
                      </Field>
                      <Field label="Company / Firm"><TextInput value={form.company} onChange={set("company")} data-testid="investor-company" /></Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Email" required error={errors.email}>
                        <TextInput type="email" value={form.email} onChange={set("email")} error={errors.email} data-testid="investor-email" />
                      </Field>
                      <Field label="Phone"><TextInput value={form.phone} onChange={set("phone")} data-testid="investor-phone" /></Field>
                    </div>
                    <Field label="I am a…">
                      <NativeSelect value={form.partner_type} onChange={set("partner_type")} data-testid="investor-type">
                        <option value="">Select…</option>
                        {PARTNER_TYPES.map((t) => <option key={t}>{t}</option>)}
                      </NativeSelect>
                    </Field>
                    <Field label="How would you like to work with us?" required error={errors.message}>
                      <TextArea rows={5} value={form.message} onChange={set("message")} error={errors.message} placeholder="Tell us about your interests, focus, and typical involvement." data-testid="investor-message" />
                    </Field>
                    <AntiSpam onChange={setSpam} testid="investor-antispam" />
                    <button type="submit" disabled={submitting} className={`${btn.gold} w-full disabled:opacity-60`} data-testid="investor-submit">
                      {submitting ? "Submitting…" : "Discuss Investment Opportunities"} {!submitting && <ArrowRight size={18} />}
                    </button>
                  </form>
                </Reveal>
              </>
            )}
          </div>
        </Container>
      </section>

      {/* DISCLAIMER */}
      <section className="pb-24" data-testid="investment-disclaimer">
        <Container>
          <Reveal>
            <div className="bg-forest/[0.03] border border-line rounded-sm p-8 md:p-10 flex gap-5 max-w-4xl mx-auto">
              <Warning size={28} weight="light" className="text-gold shrink-0 mt-1" />
              <div>
                <h3 className="font-serif text-lg font-semibold text-forest">Important Disclaimer</h3>
                <p className="mt-3 text-ink text-sm leading-relaxed">
                  The information provided on this website is for informational purposes only and does not constitute
                  an offer to sell or a solicitation of an offer to purchase any securities or investment product. Any
                  such offer or solicitation will be made only through definitive offering documents and in accordance
                  with applicable law. Nothing on this website should be construed as a representation regarding future
                  results, and no hypothetical, projected, or guaranteed returns are offered or implied. Investments in
                  real estate involve risk, including the potential loss of principal. Nothing on this website should be
                  construed as investment, legal, tax, or financial advice. Prospective investors should consult their
                  own advisors and conduct independent due diligence before making any investment decision.
                </p>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
